import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { GamingPlatform } from 'root/src/shared/rewards/constants';
import { readablePlatformsMap } from 'root/src/shared/constants/dropKeys';
import { useAppDispatch, useAppSelector } from 'root/src/client/logic/helpers/useStore';
import gamePlatformsSelector from 'root/src/client/logic/app/selectors/gamePlatformsSelector';
import modifyGamePlatforms from 'root/src/client/logic/creatorData/thunks/modifyGamePlatforms';
import { GamePlatformIcon } from 'root/src/client/v2/common/components/Games/components/GamePlatformIcon';
import { Button } from 'root/src/client/v2/common/components/ui/button';
import { toast } from 'root/src/client/v2/common/components/ui/sonner';
import { cn } from 'root/src/client/v2/common/helpers/cn';
const PLATFORMS = Object.values(GamingPlatform);
export const GamePlatformsPanel = () => {
    const dispatch = useAppDispatch();
    const current = useAppSelector(gamePlatformsSelector);
    const [selected, setSelected] = useState(current !== null && current !== void 0 ? current : []);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [failed, setFailed] = useState(false);
    const saveTimer = useRef(null);
    const save = (platforms) => {
        setSaving(true);
        setFailed(false);
        const promise = Promise.resolve(dispatch(modifyGamePlatforms(platforms)));
        toast.promise(promise, {
            loading: 'Saving platforms...',
            success: 'Game platforms saved',
            error: 'Could not save platforms. Please try again.',
        });
        promise.then(() => {
            setSaving(false);
            setSaved(true);
            setTimeout(() => setSaved(false), 1300);
        }, () => {
            setSaving(false);
            setFailed(true);
            setTimeout(() => setFailed(false), 1300);
        });
    };
    // Auto-save on toggle (debounced); the Save button also saves immediately.
    const toggle = (platform) => setSelected((prev) => {
        const next = prev.includes(platform) ? prev.filter(item => item !== platform) : [...prev, platform];
        if (saveTimer.current) {
            clearTimeout(saveTimer.current);
        }
        saveTimer.current = setTimeout(() => save(next), 800);
        return next;
    });
    const handleSave = () => {
        if (saveTimer.current) {
            clearTimeout(saveTimer.current);
        }
        save(selected);
    };
    return (<div>
			<div className="mb-1 flex items-center justify-between gap-2">
				<h3 className="text-sm font-semibold">Game Platforms</h3>
				<Button variant="outline" size="sm" loading={saving} success={saved} error={failed} onClick={handleSave} className="h-8 min-w-[120px] rounded-lg text-[11px]">
					Save platforms
				</Button>
			</div>
			<p className="mb-4 text-xs text-muted-foreground">Tell us where you stream so we deliver drops that work for you.</p>
			<div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
				{PLATFORMS.map((platform) => {
            const isSelected = selected.includes(platform);
            return (<motion.button key={platform} type="button" onClick={() => toggle(platform)} whileTap={{ scale: 0.97 }} className={cn('relative flex flex-col items-center justify-center gap-2 rounded-xl border border-solid p-4 text-xs font-medium transition-colors', isSelected
                    ? 'border-accent/40 bg-accent/10 text-foreground'
                    : 'border-border/50 bg-secondary/20 text-muted-foreground hover:border-border hover:text-foreground')}>
							<GamePlatformIcon platform={platform} className={cn('h-6 w-6', isSelected && 'text-accent')}/>
							<span className="text-center">{readablePlatformsMap[platform]}</span>
							<AnimatePresence>
								{isSelected && (<motion.span initial={{ opacity: 0, scale: 0.4 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.4 }} transition={{ duration: 0.15, ease: 'easeOut' }} className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-accent shadow-sm">
										<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#15171c" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
											<path d="M20 6 9 17l-5-5"/>
										</svg>
									</motion.span>)}
							</AnimatePresence>
						</motion.button>);
        })}
			</div>
		</div>);
};
